import type { ComponentProps } from 'react';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import AKFeedbackCollector, { FeedbackFlag } from '@atlaskit/feedback-collector';
import type { FlagProps } from '@atlaskit/flag/types';
import FlagGroup from '@atlaskit/flag/flag-group';

import { FeedbackType } from '@atlassian/company-hub-common/types';

import type { DialogsContainerProps } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

const ENTRYPOINT_ID: string = 'deea6b68-cfd3-4fd1-b1d8-c857d919f9ad';
const FEEDBACK_CONTEXT_FIELD_ID: string = 'customfield_10047';

export const i18n: {
	modalTitleSpecific: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	modalTitleGeneric: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	modalPreambleFirstLine: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	modalSummaryPlaceholder: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	modalTitleSpecific: {
		id: 'company-hub-utils.feedback-collector.title-specific',
		defaultMessage:
			'{type, select, Cards {We’d love feedback on Cards} Carousel {We’d love feedback on Carousel} Linksmenu {We’d love feedback on Links menu} Spotlight {We’d love feedback on Spotlight} Section {We’d love feedback on Section} Companyhub {We’d love feedback on Company hub} other {We’d love feedback}}',
		description:
			'Title text in modal shown to user when asking for feedback on a specific type of editor element',
	},
	modalTitleGeneric: {
		id: 'company-hub-utils.feedback-collector.title-generic',
		defaultMessage: 'We’d love feedback',
		description: 'Generic title text in modal shown to user when asking for feedback.',
	},
	modalPreambleFirstLine: {
		id: 'company-hub-utils.feedback-collector.preamble.first-line',
		defaultMessage:
			'Your input is valuable and allows Atlassian to better meet your needs. What about this feature could be improved?{lineBreak}',
		description:
			'First line of text in the feedback collector modal which is above the input box where feedback will be typed.',
	},
	modalSummaryPlaceholder: {
		id: 'company-hub-utils.feedback-collector.summary-placeholder',
		defaultMessage: 'What works well? What could be improved?',
		description: 'Placeholder text inside input box of feedback collector.',
	},
});

type FeedbackCollectorProps = DialogsContainerProps & {
	source: string;
	type: FeedbackType;
};

const FeedbackCollectorComponent = ({
	flags,
	onClose,
	onCloseComplete,
	source,
	type,
}: FeedbackCollectorProps & WithFlagsProps) => {
	const intl = useIntl();
	const { displayName, userId, locale } = useSessionData();

	const onSubmit = () => {
		void flags.showFlag({
			customComponent: DismissibleFeedbackFlag,
		});

		// Though AKFeedbackCollector uses modal-dialog internally, it doesn't expose its onCloseComplete callback. Since onCloseComplete is only necessary to avoid memory leaks, the delayed invocation of onSubmit fits the purpose.
		onCloseComplete?.();
	};

	// replacing whitespace from type is necessary, because there are FeedbackTypes that are whitespaced
	// i18n selects do not validate options with whitespace as in i18n.modalTitle above
	const feedbackTitle: string =
		type === FeedbackType.CUSTOMIZE_NAVIGATION
			? intl.formatMessage(i18n.modalTitleGeneric)
			: intl.formatMessage(i18n.modalTitleSpecific, { type: type.replace(/\s/g, '') });

	return (
		<AKFeedbackCollector
			locale={locale}
			onClose={onClose}
			onSubmit={onSubmit}
			atlassianAccountId={userId || ''}
			name={displayName || ''}
			entrypointId={ENTRYPOINT_ID}
			feedbackTitle={feedbackTitle}
			feedbackTitleDetails={
				<>
					{intl.formatMessage(i18n.modalPreambleFirstLine, {
						lineBreak: (
							<>
								<br />
								<br />
							</>
						),
					})}
				</>
			}
			summaryPlaceholder={intl.formatMessage(i18n.modalSummaryPlaceholder)}
			additionalFields={[
				{
					id: FEEDBACK_CONTEXT_FIELD_ID,
					value: source,
				},
			]}
		/>
	);
};

export const FeedbackCollector: React.FC<
	Omit<
		DialogsContainerProps & {
			source: string;
			type: FeedbackType;
		},
		'flags'
	>
> = withTransparentErrorBoundary({
	attribution: Attribution.MIGRATIONS_TOOLING,
})(withFlags(FeedbackCollectorComponent));

/**
 * Because `FeedbackFlag` does not accept an `id` prop, using it directly with `@confluence/flags` would result in an undismissible flag.
 */
function DismissibleFeedbackFlag({ id, onDismissed }: FlagProps) {
	const [displayFlag, setDisplayFlag] = useState(true);

	const onFlagGroupDismissed: ComponentProps<typeof FlagGroup>['onDismissed'] = (
		_,
		analyticsEvent,
	) => {
		setDisplayFlag(false);
		onDismissed?.(id, analyticsEvent);
	};

	return (
		<FlagGroup onDismissed={onFlagGroupDismissed}>{displayFlag && <FeedbackFlag />}</FlagGroup>
	);
}
